import { useCallback, useEffect, useState } from "react";
import { fmtMoney, fmtMoneyCompact } from "@/lib/format";

// Shared "compact money" preference used by Dashboard, Reports, and
// Reconciliation. Persists to localStorage so the choice survives
// refreshes, syncs across tabs via the storage event, and syncs within
// the same tab via a custom `ll:compact-money-change` event (which the
// native storage event does NOT fire for).
const STORAGE_KEY = "ll_compact_money";
const EVENT_NAME = "ll:compact-money-change";

const readSaved = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch { /* ignore */ }
  // Default: auto-compact on tiny screens, verbose otherwise.
  return typeof window !== "undefined" && window.innerWidth < 640;
};

export function useCompactMoney() {
  const [compact, setCompact] = useState(readSaved);

  useEffect(() => {
    const sync = () => setCompact(readSaved());
    const onStorage = (e) => { if (e.key === STORAGE_KEY) sync(); };
    window.addEventListener("storage", onStorage);
    window.addEventListener(EVENT_NAME, sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(EVENT_NAME, sync);
    };
  }, []);

  const toggle = useCallback(() => {
    setCompact((v) => {
      const next = !v;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      // Fire in-tab broadcast so sibling hook instances re-read.
      try { window.dispatchEvent(new Event(EVENT_NAME)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const money = useCallback(
    (n) => (compact ? fmtMoneyCompact(n) : fmtMoney(n)),
    [compact],
  );

  return { compact, toggle, money };
}
